import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { sleep } from '@/utils/helpers';
import { Observable } from "rxjs";

import { createUserWithEmailAndPassword } from '@firebase/auth';
import {
    User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signInWithPopup
} from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { firebaseAuth } from '@/firebase';
import { FormGroup } from "@angular/forms";
import { LocalDataServiceService } from './local-data-service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ConfigService } from './config.service';
import { Enquiry } from "@/models/enquiry/Enquiry";
import { BillingDetails } from "@/models/billing/BillingDetails";
import { AddNewProduct } from '@/models/add-new-product/add-new-product';
import { ImageDetails } from '@/models/image-details/image-details';
import { EnquiryDetails } from '@/modals/enquiry-details';
import { ResellerAddress } from "@/models/address/ResellerAddress";
import { ProductDetails } from '@/modals/product-details';
import { ProductVariantDetails } from '@/models/product-variant-details/product-variant-details';
import { ShippingDetails } from '@/models/shipping-details';
import { RegistrationDetails } from '@/models/login/registrationDetails';
import { ShopBillingDetails } from '@/models/billing/shoppBillingDetails';
import { CartDetails } from '@/models/cart-details/cart-details';
import { Address } from '@/models/address/Address';
import { CategoryDetails } from '@/models/category-details/category-details';
import { OrderDetails } from '@/models/order-details/order-details';
import { ProductDetailsByPagination } from "@/modals/product-details-page";
import { BarcodeDetails } from '@/models/product-variant-details/barcode-details';
import { TransactionDetails } from '@/models/billing/TransactionDetails';
import { UserActivityDetails } from '@/models/user-activity/user-activity-details';
import { ShopAddress } from '@/models/billing/ShopAddressDetails';
import { TimeTrackingDetails } from '@/models/time-tracking/time-tracking-details';
import { ExpenseDetails } from '@/models/expense-details/expense-details';
import { DailyExpenseRequest } from '@/models/expense-details/daily-expense-request';
import { DailyExpense } from '@/models/expense-details/daily-expense';

const provider = new GoogleAuthProvider();

@Injectable({
    providedIn: 'root'
})
export class AppService {
    public user?: User | null = null;
    private endPoint: string;

    constructor(
        private router: Router,
        private toastr: ToastrService,
        private localData: LocalDataServiceService,
        private http: HttpClient,
        private configService: ConfigService
    ) {
        onAuthStateChanged(
            firebaseAuth,
            (user) => {
                if (user) {
                    this.user = user;
                } else {
                    this.user = undefined;
                }
            },
            (e) => {
                this.user = undefined;
            }
        );
    }

    setUser(user: any) {
        this.user = user;
    }

    clearUser() {
        this.user = null;
    }

    isAdmin(): boolean {
        return localStorage.getItem('userType') === 'ADMIN';
    }

    async registerWithEmail(email: string, password: string) {
        try {
            const result = await createUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);
            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async loginWithEmail(email: string, password: string) {
        try {
            const result = await signInWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async signInByGoogle() {
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            this.user = result.user;
            console.log(result);
            this.router.navigate(['/main/dashboard']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async getProfile() {
        try {
            await sleep(500);
            const user = firebaseAuth.currentUser;
            if (user) {
                this.user = user;
            } else {
                this.logout();
            }
        } catch (error) {
            this.logout();
            this.toastr.error(error.message);
        }
    }

    async logout() {
        localStorage.clear();
        this.router.navigate(['/login']);
    }

    saveUserRegisteredDetails(userData: FormGroup): Observable<any> {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/auth/registration", userData);
    }

    loginUserData(login: FormGroup): Observable<any> {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/auth/login", login);
    }

    saveNewProduct(formGroup: FormGroup[]) {
        return this.http.post<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/add-all", formGroup,
            { headers: this.localData.getHeaders() }
        );
    }

    //Get all category details
    getAllCategoryDetails() {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/get-all",
            { headers: this.localData.getHeaders() }
        );
    }

    // Get-All Products For Website
    getAllProducts() {
        return this.http.get<AddNewProduct[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-all"
        );
    }

    // Get-All Products For Website
    getRecentProducts() {
        return this.http.get<AddNewProduct[]>(
            this.configService.getConfig().END_POINT + "/api/product/getRecentProducts"
        );
    }

    // Get live products
    getLiveProducts() {
        return this.http.get<AddNewProduct[]>(
            this.configService.getConfig().END_POINT + "/api/product/getLiveProducts"
        );
    }

    // Get products by category ID
    getProductsByCategoryId(categoryId: number) {
        return this.http.get<AddNewProduct[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-category-id/" + categoryId
        );
    }

    // Get products by subcategory ID
    getProductsBySubCategoryId(subCategoryId: number) {
        return this.http.get<AddNewProduct[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-subcategory/" + subCategoryId
        );
    }

    getAllProductsByPagination(page: number) {
        return this.http.get<ProductDetailsByPagination>(
            this.configService.getConfig().END_POINT + `/api/product/getProductsWithPagination/${page}`
        );
    }


    // category Get Details
    getCategoryDetails() {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/get-all"
        );
    }

    // category Get Details by active
    getCategoryDetailsByActive() {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/get-all-active-categories"
        );
    }

    // Update product availability status
    updateProductAvailability(jsonData: any) {
        return this.http.put(
            this.configService.getConfig().END_POINT + "/api/product/update-availability", jsonData,
            { headers: this.localData.getHeaders() }
        );
    }

    //get all enquiry list by admin
    getAllEnquiryList() {
        return this.http.get(
            this.configService.getConfig().END_POINT + "/api/enquiry/get-all",
            { headers: this.localData.getHeaders() }
        )
    }

    //update customer enquiry status by ADMIN
    updateEnquiryStatusByEnquiryId(updateStatus: any) {
        return this.http.put<Enquiry>(
            this.configService.getConfig().END_POINT + "/api/enquiry/update-status", updateStatus,
            { headers: this.localData.getHeaders() }
        )
    }

    getAllProductsByProductCode(productCode: any) {
        return this.http.get<ProductDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-all" + "/" + productCode,
            { headers: this.localData.getHeaders() }
        )
    }

    //save bill details
    saveInvoiceDetails(formGroup: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/billing/save", formGroup,
            { headers: this.localData.getHeaders() }
        )
    }

    //get bill details by BillId
    getBillDetailById(billId: number) {
        return this.http.get<BillingDetails>(
            this.configService.getConfig().END_POINT + "/api/billing/get/" + billId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Get product id details
    getProductIdByDetails(productId: number) {
        return this.http.get<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-id/" + productId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Image Id based on delete the image
    deleteImageByAssetId(imageId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/product/image-details/delete/" + imageId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Update product details
    updateProductDetails(formGroup: FormGroup) {
        return this.http.put<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/update/", formGroup,
            { headers: this.localData.getHeaders() }
        )
    }

    //Delete product variant details based on
    deleteProductVariantDetails(variantId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/product/variant/delete/" + variantId,
            { headers: this.localData.getHeaders() }
        )
    }

    //update product image
    updateProductImage(productId: number, ImageList: ImageDetails[]) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/product/image-details/add-more/" + productId, ImageList,
            { headers: this.localData.getHeaders() }
        )
    }

    //Get all bill details
    getAllBillDetails() {
        return this.http.get<BillingDetails[]>(
            this.configService.getConfig().END_POINT + "/api/billing/get-all",
            { headers: this.localData.getHeaders() }
        )
    }

    // send Enquiry
    sendEnquiry(data: EnquiryDetails) {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/api/enquiry/send", data
        );
    }

    // get product by Id
    getProductById(id: number) {
        return this.http.get<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-id/" + id
        );
    }

    // get website product by Id
    getWebsiteProductById(id: number) {
        return this.http.get<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-id/website/" + id
        );
    }

    //get all reseller details
    getAllResellerDetails() {
        return this.http.get<ResellerAddress[]>(
            this.configService.getConfig().END_POINT + "/api/address-details/getAll",
            { headers: this.localData.getHeaders() }
        )
    }

    //Invoice no based details get
    getInvoiceById(jsonData: any) {
        return this.http.post<BillingDetails>(
            this.configService.getConfig().END_POINT + "/api/billing/get/byInvoice", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Bill id based status update
    billStatusUpdated(jsonData: any) {
        return this.http.put(
            this.configService.getConfig().END_POINT + "/api/billing/update/billingStatus", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }


    //Update bill details
    updateBillDetails(formGroup: FormGroup) {
        return this.http.put<BillingDetails>(
            this.configService.getConfig().END_POINT + "/api/billing/update", formGroup,
            { headers: this.localData.getHeaders() }
        )
    }

    //BillItem details delete
    deleteIdByBillItem(billingItemId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/billing/deleteBillingItem/" + billingItemId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Update product variant details
    updateSingleVariant(formData: any) {
        return this.http.put<ProductVariantDetails>(
            this.configService.getConfig().END_POINT + "/api/product/update-price", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Get barcode deatils
    getByBarcodeDetails(id: string) {
        return this.http.get<BarcodeDetails>(
            this.configService.getConfig().END_POINT + "/api/product/get-variant/" + id,
            { headers: this.localData.getHeaders() }
        );
    }

    //Save Shipping details
    saveShippingDetails(formData: any) {
        return this.http.post<ShippingDetails>(
            this.configService.getConfig().END_POINT + "/api/shipping-details/save", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    //get all shipping details
    getAllShipping() {
        return this.http.get<ShippingDetails[]>(
            this.configService.getConfig().END_POINT + "/auth/shipping-details/get-all",
        )
    }

    //update shipping details
    updateShippingDetails(formData: any) {
        return this.http.put<ShippingDetails>(
            this.configService.getConfig().END_POINT + "/api/shipping-details/update", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    // Login send otp
    loginSendOtp(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/auth/login/email/send-otp", jsonData
        )
    }

    //verified login opt
    loginVerifyOtp(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/auth/login/email/verify-otp", jsonData
        )
    }

    // Registration send otp
    registrationSendOtp(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/auth/email/send-otp", jsonData
        )
    }

    //Registration opt verify
    registrationVerifyOtp(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/auth/email/verify-otp", jsonData
        )
    }

    //Registration save
    saveRegistration(formData: any) {
        return this.http.post<RegistrationDetails>(
            this.configService.getConfig().END_POINT + "/auth/registration", formData,
        )

    }

    //Freeze stage details
    freezeStageDetails(variantId: number, quantity: number) {
        return this.http.post<ProductVariantDetails>(
            this.configService.getConfig().END_POINT + "/api/admin/stock/freeze/" + variantId + "/" + quantity, null,
            { headers: this.localData.getHeaders() }
        )
    }

    //Unfreeze Stage Details
    unfreezeStageDetails(variantId: number, quantity: number) {
        return this.http.post<ProductVariantDetails>(
            this.configService.getConfig().END_POINT + "/api/admin/stock/unfreeze/" + variantId + "/" + quantity, null,
            { headers: this.localData.getHeaders() }
        )
    }

    //Add to cart
    addToCart(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/cartItem/add", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Product get-all variant details
    getAllVariant() {
        return this.http.get<ProductVariantDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/getAllVariants",
        );
    }

    //Product get-all variant details
    getAllWithOutZeroQtyVariant() {
        return this.http.get<ProductVariantDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-all-variants",
        );
    }

    //Product id based variant details get all
    getVariantByProductId(variantId: number) {
        return this.http.get<ProductVariantDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-variants/" + variantId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Customer cart details get
    getByCustomerCartDetails() {
        return this.http.get<CartDetails[]>(
            this.configService.getConfig().END_POINT + "/api/cart/getByUserId",
            { headers: this.localData.getHeaders() }
        );
    }

    //Customer address details get
    getByCustomerAddressDetails(userId: number) {
        return this.http.get<Address[]>(
            this.configService.getConfig().END_POINT + "/api/address-details/get/" + userId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Add new category details
    addCategoryDetails(formData: any) {
        return this.http.post<CategoryDetails>(
            this.configService.getConfig().END_POINT + "/api/category/save", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Update category details
    updateCategoryDetails(formData: any) {
        return this.http.put<CategoryDetails>(
            this.configService.getConfig().END_POINT + "/api/category/update", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Create purchase order
    createRazorpayOrder(jsonData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/razorpay/order/", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Order Conformation
    confirmOrder(formData: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/razorpay/confirm", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Cart quantity value update
    updateCartQuantity(jsonData: any) {
        return this.http.put<any>(
            this.configService.getConfig().END_POINT + "/api/cartItem/update", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Remove cart item details
    removeCartItemDetails(cartItemId: number) {
        return this.http.delete<any>(
            this.configService.getConfig().END_POINT + "/api/cartItem/remove/" + cartItemId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Barcode print page variant details get
    getVariantAndDiscountDetails() {
        return this.http.get<any>(
            this.configService.getConfig().END_POINT + "/api/product/get-variant-with-discount",
            { headers: this.localData.getHeaders() }
        );
    }

    //Category cod details id based remove
    categoryCodDetailRemove(codeDetailsId: number) {
        return this.http.delete<any>(
            this.configService.getConfig().END_POINT + "/api/category/delete-cod/" + codeDetailsId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Category shipping details id based remove
    categoryShippingDetailsRemove(shippingDetailsId: number) {
        return this.http.delete<any>(
            this.configService.getConfig().END_POINT + "/api/category/delete-shipping/" + shippingDetailsId,
            { headers: this.localData.getHeaders() }
        );
    }

    //User cart category details get by id
    getByCartCategoryDetails(cartId: number) {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/cartItem/getUserAddedCategories/" + cartId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Sub Category remove function
    subCategoryRemove(subCategoryId: number) {
        return this.http.delete<any>(
            this.configService.getConfig().END_POINT + "/api/category/delete-subcategory/" + subCategoryId,
            { headers: this.localData.getHeaders() }
        );
    }

    // update category status
    updateCategoryStatus(payload: any) {
        return this.http.put<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/update-category-status", payload,
            { headers: this.localData.getHeaders() }
        );
    }

    //user address update
    updateUserAddress(jsonData: any) {
        return this.http.put<Address[]>(
            this.configService.getConfig().END_POINT + "/api/address-details/update", jsonData,
            { headers: this.localData.getHeaders() }
        )
    }

    //Customer get my orders
    getMyOrdersDetails() {
        return this.http.get<OrderDetails>(
            this.configService.getConfig().END_POINT + "/api/orders/my-orders",
            { headers: this.localData.getHeaders() }
        );
    }

    //Status based order details get all
    getByStatusOrderDetails(orderStatus: string) {
        return this.http.get<OrderDetails[]>(
            this.configService.getConfig().END_POINT + "/api/orders/getAllByOrderStatus/" + orderStatus,
            { headers: this.localData.getHeaders() }
        );
    }


    /* UPDATE SINGLE ITEM */
    updateOrderItemStatus(jsonData: any) {
        return this.http.put(
            this.configService.getConfig().END_POINT + "/api/orders/update-orderItem-status", jsonData,
            { headers: this.localData.getHeaders() }
        );
    }

    /* BULK UPDATE */
    updateBulkOrderItems(jsonData: any) {
        return this.http.put(
            this.configService.getConfig().END_POINT + '/api/orders/update-status', jsonData,
            { headers: this.localData.getHeaders() }
        );
    }

    // Search products by keyword
    searchProducts(keyword: string): Observable<any[]> {
        return this.http.get<any[]>(
            this.configService.getConfig().END_POINT + "/api/product/search",
            { params: { searchKeyword: keyword } }
        );
    }

    //Get All Variant Details
    getAllVariantDetails() {
        return this.http.get<any[]>(
            this.configService.getConfig().END_POINT + "/api/product/getAllVariants",
            { headers: this.localData.getHeaders() }
        );
    }

    //Category details get by id
    categoryGetById(jsonData: any) {
        const params = new HttpParams()
            .set('categoryId', jsonData.categoryId);
        return this.http.get<CategoryDetails>(
            this.configService.getConfig().END_POINT + "/api/category/get-by-id",
            { params }
        );
    }

    //Product discount id based details remove
    deleteDiscountById(discountId: Number) {
        return this.http.delete<any>(
            this.configService.getConfig().END_POINT + "/api/product/discount/delete/" + discountId,
            { headers: this.localData.getHeaders() }
        );
    }

    //variant id based image id getting
    variantIdBasedImageDetailsGetting(variantId: number) {
        return this.http.get<any>(
            this.configService.getConfig().END_POINT + "/api/product/get-image-id/" + variantId,
            { headers: this.localData.getHeaders() }
        );
    }

    //billing details get by start and end date
    getBillingDetailsByStartAndEndDate(startDate: number, endDate: number) {
        return this.http.get<BillingDetails[]>(
            this.configService.getConfig().END_POINT + "/api/billing/getAllByDates/" + startDate + "/" + endDate,
            { headers: this.localData.getHeaders() }
        );
    }

    //Get all without zero qty variant
    getAllWithZeroQtyVariant(startDate: number, endDate: number) {
        return this.http.get<ProductVariantDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-variant/out-of-stock/" + startDate + "/" + endDate,
            { headers: this.localData.getHeaders() }
        );
    }

    //user activity details get by start and end date
    getActivityDetailsByStartAndEndDate(startDate: number, endDate: number) {
        return this.http.get<UserActivityDetails[]>(
            this.configService.getConfig().END_POINT + "/api/user-tracking/getAllByDates/" + startDate + "/" + endDate,
            { headers: this.localData.getHeaders() }
        );
    }

    //shop address details get all
    getAllShopAddressDetails() {
        return this.http.get<ShopAddress[]>(
            this.configService.getConfig().END_POINT + "/api/billing/getAllShopAddresses",
            { headers: this.localData.getHeaders() }
        );
    }

    //Free stage without 0 variant details get all
    getAllFreeStageVariant() {
        return this.http.get<ProductVariantDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-available-variants",
            { headers: this.localData.getHeaders() }
        );
    }

    //Order Id based tracking time details get
    getTimeTrackingByOrderId(orderId: number) {
        return this.http.get<TimeTrackingDetails[]>(
            this.configService.getConfig().END_POINT + "/api/orders/get-tracking/" + orderId,
            { headers: this.localData.getHeaders() }
        );
    }

    //Delete product image details
    deleteProductById(productId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/product/variants/delete-by-product/" + productId,
            { headers: this.localData.getHeaders() }
        );
    }

    //User phone number based user address get
    getUserByPhoneNumber(phoneNumber: any) {
        return this.http.get<RegistrationDetails>(
            this.configService.getConfig().END_POINT + "/auth/registration/phone/" + phoneNumber,
            { headers: this.localData.getHeaders() }
        );
    }

    //Update admin bill status
    updateAdminStatus(data: any) {
        return this.http.put<any>(
            this.configService.getConfig().END_POINT + "/api/billing/updateAdminStatus", data,
            { headers: this.localData.getHeaders() }
        );
    }

    // User tracking add
    saveUserTracking(data: any) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/auth/payment/track", data,
            { headers: this.localData.getHeaders() }
        );
    }

    /* ------------------- EXPENSE ------------------- */
    saveExpenseDetail(formData: FormData): Observable<ExpenseDetails> {
        return this.http.post<ExpenseDetails>(
            this.configService.getConfig().END_POINT + "/api/expense-details/save", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    getAllExpenseDetails(): Observable<ExpenseDetails[]> {
        return this.http.get<ExpenseDetails[]>(
            this.configService.getConfig().END_POINT + "/api/expense-details/get-all",
            { headers: this.localData.getHeaders() }
        )
    }

    updateExpenseDetail(formData: FormData): Observable<ExpenseDetails> {
        return this.http.put<ExpenseDetails>(
            this.configService.getConfig().END_POINT + "/api/expense-details/update", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    updateExpenseStatus(updateStatus: any): Observable<any> {
        return this.http.put(
            this.configService.getConfig().END_POINT + "/api/expense-details/update-expense-status", updateStatus,
            { headers: this.localData.getHeaders() }
        )
    }

    getExpenseDetailById(expenseId: number): Observable<ExpenseDetails> {
        return this.http.get<ExpenseDetails>(
            this.configService.getConfig().END_POINT + "/api/expense-details/getById/" + expenseId,
            { headers: this.localData.getHeaders() }
        )
    }

    saveDailyExpense(formData: any): Observable<DailyExpenseRequest> {
        return this.http.post<DailyExpenseRequest>(
            this.configService.getConfig().END_POINT + "/api/daily-expense", formData,
            { headers: this.localData.getHeaders() }
        )
    }
    
    updateDailyExpense(formData: any): Observable<DailyExpenseRequest> {
        return this.http.put<DailyExpenseRequest>(
            this.configService.getConfig().END_POINT + "/api/daily-expense", formData,
            { headers: this.localData.getHeaders() }
        )
    }

    getDailyExpenseById(dailyExpenseId: number): Observable<DailyExpense> {
        return this.http.get<DailyExpense>(
            this.configService.getConfig().END_POINT + "/api/daily-expense/" + dailyExpenseId,
            { headers: this.localData.getHeaders() }
        )
    }

    getDailyExpensesByDateRange(startDate: number, endDate: number) {
        return this.http.get<DailyExpense[]>(
            this.configService.getConfig().END_POINT + "/api/daily-expense/date-range/" + startDate + "/" + endDate,
            { headers: this.localData.getHeaders() }
        );
    }

    deleteDailyExpense(dailyExpenseId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/daily-expense/" + dailyExpenseId,
            { headers: this.localData.getHeaders() }
        );
    }

    getDailyExpenseFile(dailyExpenseId: number) {
        return this.http.get(
            this.configService.getConfig().END_POINT + "/api/daily-expense/get-file/" + dailyExpenseId,
            
            { headers: this.localData.getHeaders(),responseType: 'blob' }
        );
    }

    deleteDailyExpenseFile(dailyExpenseId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/daily-expense/delete-file/" + dailyExpenseId,
            { headers: this.localData.getHeaders() }
        );
    }

}


